/**
 * ProjectsList — grid of projects with create/edit modal (managers only).
 */
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Users, ListChecks, X, Trash2, Pencil, MoreVertical, FolderOpen } from "lucide-react";
import { wsApi, wsBase, isManager } from "./wsApi"; // jiraApi — parked
import { localDateStr } from "../../utils/localDate";
import { PROJECT_STATUS, PRIORITY, BUDGET_TYPES, fmtDate } from "./wsConstants";
import {
  T,
  PrimaryButton,
  StatusBadge,
  SearchInput,
  ConfirmDialog,
  selectChevron,
} from "../../components/em/ui";
import { Pills, ProgressBar, ColorBadge, LogoPicker, AvatarStack, SkeletonGrid, hoverLift, hoverLiftStyle } from "./wsUi";
import { assetUrl } from "./wsApi";

const EMPTY = {
  name: "",
  description: "",
  client_id: "",
  status: "planning",
  priority: "medium",
  color: "#f54b01",
  deadline: "",
  start_date: "",
  budget: "",
  hourly_quote: "",
  budget_type: "hourly",
  logo: "",
  // jira_project_key: "",   // PARKED with the Jira integration
};

// Dates arrive from the API as full ISO instants ("2027-07-12T00:00:00.000Z").
// An <input type="date"> shows nothing for those, so the edit form would open
// with its dates blank and save them away. Slicing the string is not enough
// either: east of UTC the ISO day is the day before, so the local day is used.
const toDayInput = (value) => {
  if (!value) return "";
  const s = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  return isNaN(d) ? "" : localDateStr(d);
};

function ProjectModal({ initial, clients, existing = [], onClose, onSaved }) {
  const editing = !!initial?.id;
  const [form, setForm] = useState(() => ({
    ...EMPTY,
    ...(initial || {}),
    start_date: toDayInput(initial?.start_date),
    deadline: toDayInput(initial?.deadline),
    // Nulls from the API would make these inputs uncontrolled.
    description: initial?.description ?? "",
    client_id: initial?.client_id ?? "",
    budget: initial?.budget ?? "",
    hourly_quote: initial?.hourly_quote ?? "",
    logo: initial?.logo ?? "",
  }));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  /* ── Jira lookup state (PARKED — see the field below) ──────────────────────
  // Jira lookup state for the key field: null = nothing tried yet.
  const [jira, setJira] = useState({ loading: false, name: null, error: null });
  // The key the current result belongs to, so an edited key clears the stale
  // confirmation instead of leaving a name from the previous lookup on screen.
  const [jiraCheckedKey, setJiraCheckedKey] = useState(
    initial?.jira_project_key ? String(initial.jira_project_key).toUpperCase() : "",
  );

  const jiraKey = String(form.jira_project_key || "").trim().toUpperCase();

  // Resolve the key against Jira and adopt the project's name. Optional field:
  // a failure shows inline and never blocks the rest of the form.
  const fetchJira = async () => {
    if (!jiraKey) return setJira({ loading: false, name: null, error: null });
    setJira({ loading: true, name: null, error: null });
    try {
      const res = await jiraApi.lookupProject(jiraKey);
      setJiraCheckedKey(res.key);
      set("jira_project_key", res.key);
      // Auto-fill the name — but never clobber a name the user already typed.
      if (!form.name.trim()) set("name", res.name);
      setJira({
        loading: false,
        name: res.name,
        error:
          res.linked_project && res.linked_project.id !== initial?.id
            ? `Already linked to "${res.linked_project.name}"`
            : null,
      });
    } catch (e) {
      setJiraCheckedKey(jiraKey);
      setJira({
        loading: false,
        name: null,
        error: e?.response?.data?.error || "Could not reach Jira",
      });
    }
  };
  ── end of parked Jira lookup ── */

  const save = async () => {
    if (!form.name.trim()) return toast.error("Project name is required");
    // Names are unique workspace-wide. The server enforces this and returns a
    // 409; catching it here first saves a round-trip. Compared case- and
    // space-insensitively, skipping this project so a rename to its own name
    // is fine.
    const key = (s) => String(s ?? "").trim().toLowerCase();
    const clash = existing.find(
      (p) => p.id !== initial?.id && key(p.name) === key(form.name),
    );
    if (clash) return toast.error(`A project named "${clash.name}" already exists.`);
    // `min` on the input only constrains the picker — a typed or pasted date
    // gets through, so the pair is compared here too. Plain string compare is
    // safe and timezone-free on 'YYYY-MM-DD'.
    if (
      form.start_date &&
      form.deadline &&
      String(form.deadline).slice(0, 10) < String(form.start_date).slice(0, 10)
    ) {
      return toast.error("Deadline can't be earlier than the project start date");
    }
    setSaving(true);
    try {
      const body = {
        ...form,
        client_id: form.client_id || null,
        deadline: form.deadline || null,
        // PARKED with the Jira field above:
        // "" would fail the UNIQUE check on the second unlinked project.
        // jira_project_key: jiraKey || null,
      };
      if (editing) await wsApi.updateProject(initial.id, body);
      else await wsApi.createProject(body);
      toast.success(editing ? "Project updated" : "Project created");
      onSaved();
    } catch (e) {
      // Surface the server's message — "already linked to X" and the
      // key-format error are both actionable, unlike a generic failure.
      toast.error(e?.response?.data?.error || "Could not save project");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <div style={modalHead}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>
            {editing ? "Edit Project" : "New Project"}
          </h3>
          <button onClick={onClose} style={iconBtn}>
            <X size={18} />
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Field label="Logo">
            <LogoPicker value={form.logo} onChange={(v) => set("logo", v)} color={form.color} label={form.name} />
          </Field>
          <Field label="Project Name *">
            <input
              style={input}
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="e.g. Website Redesign"
            />
          </Field>
          {/* ── Jira link (PARKED — waiting on the Jira site URL + API token) ──
              The backend route and the jira_project_key column are both
              commented out, so this field has nowhere to save to and the lookup
              would 404. Uncomment together with jiraApi in wsApi.js, the
              jira_project_key entries in EMPTY + save(), and the lookup state
              above.

              Jira link — optional. The project KEY ("CR"), not an issue key
              ("CR-66"): it routes every worklog under that key to this project.
              Sits under the name because a successful lookup fills the name in.

          <Field label="Jira Project Key">
            <div style={{ display: "flex", gap: 8 }}>
              <input
                style={{ ...input, textTransform: "uppercase", flex: 1 }}
                value={form.jira_project_key || ""}
                onChange={(e) => {
                  set("jira_project_key", e.target.value);
                  // Editing invalidates the previous result.
                  if (jira.name || jira.error) setJira({ loading: false, name: null, error: null });
                }}
                onBlur={() => {
                  if (jiraKey && jiraKey !== jiraCheckedKey) fetchJira();
                }}
                placeholder="e.g. CR — leave blank if this project isn't in Jira"
              />
              <button
                type="button"
                onClick={fetchJira}
                disabled={!jiraKey || jira.loading}
                style={{
                  ...ghostBtn,
                  whiteSpace: "nowrap",
                  cursor: !jiraKey || jira.loading ? "not-allowed" : "pointer",
                  opacity: !jiraKey || jira.loading ? 0.6 : 1,
                }}
              >
                {jira.loading ? "Checking…" : "Fetch from Jira"}
              </button>
            </div>
            {jira.name && (
              <div style={{ marginTop: 6, font: `500 11px ${T.font}`, color: T.success600 }}>
                ✓ {jira.name}
                {jira.error ? "" : " — Jira time under this key syncs to this project"}
              </div>
            )}
            {jira.error && (
              <div style={{ marginTop: 6, font: `500 11px ${T.font}`, color: T.danger }}>
                {jira.error}
              </div>
            )}
          </Field>
          ── end of parked Jira field ── */}

          <Field label="Description">
            <textarea
              style={{ ...input, minHeight: 70, resize: "vertical" }}
              value={form.description || ""}
              onChange={(e) => set("description", e.target.value)}
              placeholder="Short summary (full document lives on the project page)"
            />
          </Field>

          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
          >
            <Field label="Client">
              <select
                style={selectInput}
                value={form.client_id || ""}
                onChange={(e) => set("client_id", e.target.value)}
              >
                <option value="">— No client —</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.company_name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Project Start">
              <input
                type="date"
                style={input}
                value={form.start_date ? String(form.start_date).slice(0, 10) : ""}
                onChange={(e) => set("start_date", e.target.value)}
              />
            </Field>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="Deadline">
              <input
                type="date"
                style={input}
                // Greys out everything before the start date in the picker.
                min={form.start_date ? String(form.start_date).slice(0, 10) : undefined}
                value={form.deadline ? String(form.deadline).slice(0, 10) : ""}
                onChange={(e) => set("deadline", e.target.value)}
              />
            </Field>
            <Field label="Budget Type">
              <select style={selectInput} value={form.budget_type || "hourly"} onChange={(e) => set("budget_type", e.target.value)}>
                {BUDGET_TYPES.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
              </select>
            </Field>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="Budget"><input type="number" style={input} value={form.budget} onChange={(e) => set("budget", e.target.value)} placeholder="Inherits client if blank" /></Field>
            <Field label="Hourly Quote"><input type="number" style={input} value={form.hourly_quote} onChange={(e) => set("hourly_quote", e.target.value)} placeholder="Inherits client if blank" /></Field>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 80px",
              gap: 12,
            }}
          >
            <Field label="Status">
              <select
                style={selectInput}
                value={form.status || 'planning'}
                onChange={(e) => set("status", e.target.value)}
              >
                {Object.entries(PROJECT_STATUS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Priority">
              <select
                style={selectInput}
                value={form.priority || 'medium'}
                onChange={(e) => set("priority", e.target.value)}
              >
                {Object.entries(PRIORITY).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Color">
              <input
                type="color"
                style={{ ...input, padding: 2, height: 38, cursor: "pointer" }}
                value={form.color}
                onChange={(e) => set("color", e.target.value)}
              />
            </Field>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            marginTop: 22,
          }}
        >
          <button onClick={onClose} style={ghostBtn}>
            Cancel
          </button>
          <button onClick={save} disabled={saving} style={primaryBtn}>
            {saving ? "Saving…" : editing ? "Save Changes" : "Create Project"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ProjectsList() {
  const navigate = useNavigate();
  const manager = isManager();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [modal, setModal] = useState(null); // null | {} | project
  const [menuFor, setMenuFor] = useState(null); // id of the card whose ⋮ is open

  // Any click or Escape outside the open menu closes it.
  useEffect(() => {
    if (menuFor == null) return undefined;
    const close = () => setMenuFor(null);
    const onKey = (e) => e.key === "Escape" && close();
    window.addEventListener("click", close);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("keydown", onKey);
    };
  }, [menuFor]);

  const load = useCallback(() => {
    setLoading(true);
    wsApi
      .listProjects()
      .then(setProjects)
      .catch(() => toast.error("Could not load projects"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    if (manager)
      wsApi
        .listClients()
        .then(setClients)
        .catch(() => {});
  }, [load, manager]);

  // The card the user is deleting, held while the dialog is up.
  const [confirming, setConfirming] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const del = async () => {
    if (!confirming) return;
    setDeleting(true);
    try {
      await wsApi.deleteProject(confirming.id);
      setProjects((p) => p.filter((x) => x.id !== confirming.id));
      toast.success("Project deleted");
      setConfirming(null);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const filtered = projects.filter(
    (p) =>
      (filter === "all" || p.status === filter) &&
      (!search || p.name.toLowerCase().includes(search.toLowerCase())),
  );

  const FILTERS = [
    { value: "all", label: "All" },
    { value: "planning", label: "Planning" },
    { value: "active", label: "Active" },
    { value: "on_hold", label: "On Hold" },
    { value: "completed", label: "Completed" },
  ];

  return (
    <div>
      {/* Toolbar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 18,
          flexWrap: "wrap",
        }}
      >
        <Pills options={FILTERS} value={filter} onChange={setFilter} />
        <SearchInput
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search projects…"
          style={{ minWidth: 200 }}
        />
        <div style={{ flex: 1 }} />
        {manager && (
          <PrimaryButton icon={Plus} onClick={() => setModal({})}>
            New Project
          </PrimaryButton>
        )}
      </div>

      {loading ? (
        <SkeletonGrid count={6} minWidth={300} height={190} />
      ) : filtered.length === 0 ? (
        <div
          style={{ textAlign: "center", padding: "60px 20px", color: T.fg7 }}
        >
          <FolderOpen size={40} color={T.fg7} style={{ opacity: 0.5 }} />
          <div
            style={{ font: `600 15px ${T.font}`, color: T.fg1, marginTop: 12 }}
          >
            {search || filter !== "all"
              ? "No projects match your filters"
              : "No projects yet"}
          </div>
          <div style={{ font: `400 13px ${T.font}`, marginTop: 4 }}>
            {manager
              ? "Create your first project to get started."
              : "Projects you’re on will show up here."}
          </div>
          {manager && filter === "all" && !search && (
            <div style={{ marginTop: 16, display: "inline-flex" }}>
              <PrimaryButton icon={Plus} onClick={() => setModal({})}>
                New Project
              </PrimaryButton>
            </div>
          )}
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
            gap: 16,
          }}
        >
          {filtered.map((p) => {
            const progress =
              p.task_count > 0
                ? Math.round((p.done_count / p.task_count) * 100)
                : 0;
            return (
              <div
                key={p.id}
                onClick={() => navigate(`${wsBase()}/projects/${p.id}`)}
                {...hoverLift}
                style={{
                  background: "var(--em-bg-card)",
                  border: `1px solid ${T.border}`,
                  borderRadius: 14,
                  padding: 18,
                  boxShadow: T.shadowSm,
                  cursor: "pointer",
                  ...hoverLiftStyle,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 12,
                    marginBottom: 14,
                  }}
                >
                  {p.logo ? (
                    <div style={{ width: 40, height: 40, borderRadius: 9, overflow: "hidden", flexShrink: 0, border: `1px solid ${T.border}` }}>
                      <img src={assetUrl(p.logo)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    </div>
                  ) : (
                    <ColorBadge color={p.color || T.orange} size={40} label={(p.name || "").slice(0, 1).toUpperCase()} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        font: `700 14px ${T.font}`,
                        color: T.fg1,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {p.name}
                    </div>
                    <div
                      style={{
                        font: `400 11px ${T.font}`,
                        color: T.fg7,
                        marginTop: 2,
                      }}
                    >
                      {p.client_name || "No client"}
                    </div>
                  </div>
                  {/* Status and the ⋮ read as one control group: a tight gap
                      between them, centred on each other, so the badge doesn't
                      float away from the menu it sits beside. */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      flexShrink: 0,
                    }}
                  >
                    <StatusBadge
                      label={
                        (PROJECT_STATUS[p.status] || PROJECT_STATUS.planning)
                          .label
                      }
                    />
                    {manager && (
                      <CardMenu
                        open={menuFor === p.id}
                        onToggle={() =>
                          setMenuFor((cur) => (cur === p.id ? null : p.id))
                        }
                        label={p.name}
                        items={[
                          // HR is admin-tier, and PUT /projects/:id already
                          // accepts it server-side — this was a UI-only gate.
                          manager && {
                            key: "edit",
                            icon: Pencil,
                            label: "Edit",
                            onClick: () => setModal(p),
                          },
                          manager && {
                            key: "delete",
                            icon: Trash2,
                            label: "Delete",
                            danger: true,
                            onClick: () => setConfirming(p),
                          },
                        ].filter(Boolean)}
                      />
                    )}
                  </div>
                </div>
                {p.description && (
                  <p
                    style={{
                      margin: "0 0 16px",
                      font: `400 12px/17px ${T.font}`,
                      color: T.fg5,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {p.description}
                  </p>
                )}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 14,
                  }}
                >
                  <ProgressBar pct={progress} color={p.color || T.orange} />
                  <span
                    style={{
                      font: `700 11px ${T.font}`,
                      color: T.fg4,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {progress}%
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    paddingTop: 14,
                    borderTop: `1px solid ${T.divider}`,
                  }}
                >
                  {p.members && p.members.length ? (
                    <AvatarStack members={p.members} max={4} size={26} />
                  ) : (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 5,
                        font: `500 11px ${T.font}`,
                        color: T.fg5,
                      }}
                    >
                      <Users size={13} /> {p.member_count}
                    </span>
                  )}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 14,
                      font: `500 11px ${T.font}`,
                      color: T.fg5,
                    }}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      <ListChecks size={13} /> {p.done_count}/{p.task_count}
                    </span>
                    {p.deadline && <span>{fmtDate(p.deadline)}</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modal && (
        <ProjectModal
          initial={modal.id ? modal : null}
          clients={clients}
          existing={projects}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            load();
          }}
        />
      )}

      <ConfirmDialog
        open={!!confirming}
        title="Delete project?"
        message={`"${confirming?.name}" and all its tasks will be removed. This cannot be undone.`}
        confirmLabel="Delete project"
        danger
        busy={deleting}
        onConfirm={del}
        onCancel={() => !deleting && setConfirming(null)}
      />
    </div>
  );
}

/**
 * The card's ⋮ menu. Every click inside stops propagation — the whole card is
 * a link to the project, so without that, opening the menu would navigate away.
 */
function CardMenu({ open, onToggle, items, label }) {
  return (
    <div style={{ position: "relative", flexShrink: 0 }}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        title="Project actions"
        aria-label={`Actions for ${label}`}
        aria-haspopup="menu"
        aria-expanded={open}
        style={{
          width: 26,
          height: 26,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 8,
          border: `1px solid ${open ? T.border : "transparent"}`,
          background: open ? "var(--em-bg-head)" : "none",
          color: T.fg5,
          cursor: "pointer",
          padding: 0,
        }}
      >
        <MoreVertical size={15} />
      </button>
      {open && (
        <div
          role="menu"
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            minWidth: 140,
            background: "var(--em-bg-card)",
            border: `1px solid ${T.border}`,
            borderRadius: 10,
            boxShadow: T.shadowMd,
            padding: 4,
            zIndex: 20,
          }}
        >
          {items.map(({ key, icon: Icon, label: text, danger, onClick }) => (
            <button
              key={key}
              type="button"
              role="menuitem"
              onClick={(e) => {
                e.stopPropagation();
                onToggle(); // close first — the row may vanish on delete
                onClick(e);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                width: "100%",
                padding: "7px 10px",
                borderRadius: 7,
                border: 0,
                background: "none",
                color: danger ? T.danger : T.fg2,
                font: `500 12.5px ${T.font}`,
                cursor: "pointer",
                textAlign: "left",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--em-bg-head)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
            >
              <Icon size={13} /> {text}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Shared bits ────────────────────────────────────────────────────────────
function Field({ label, children }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--em-fg2)" }}>
        {label}
      </span>
      {children}
    </label>
  );
}

const input = {
  width: "100%",
  border: "1px solid var(--em-border)",
  borderRadius: 8,
  padding: "9px 11px",
  fontSize: 13.5,
  outline: "none",
  boxSizing: "border-box",
  fontFamily: "inherit",
  background: "var(--em-bg-card)",
  color: "var(--em-fg1)",
};
// Same field chrome plus the design system's dropdown chevron (<select> only).
const selectInput = { ...input, ...selectChevron };
const overlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.5)",
  zIndex: 1000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 20,
};
const modal = {
  background: "var(--em-bg-card)",
  borderRadius: 14,
  padding: "22px 24px",
  width: "100%",
  maxWidth: 520,
  maxHeight: "90vh",
  overflowY: "auto",
  boxShadow: "0 24px 64px rgba(0,0,0,0.2)",
};
const modalHead = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 18,
};
const iconBtn = {
  background: "none",
  border: "none",
  cursor: "pointer",
  display: "flex",
  padding: 4,
};
// const emptyBox = {
//   background: "var(--em-bg-card)",
//   border: "1px dashed var(--em-border)",
//   borderRadius: 12,
//   padding: 50,
//   textAlign: "center",
// };
const primaryBtn = {
  display: "flex",
  alignItems: "center",
  gap: 7,
  background: "#f54b01",
  color: "#fff",
  border: "none",
  borderRadius: 8,
  padding: "9px 16px",
  cursor: "pointer",
  fontSize: 13,
  fontWeight: 600,
};
const ghostBtn = {
  display: "flex",
  alignItems: "center",
  gap: 7,
  background: "var(--em-bg-card)",
  color: "var(--em-fg2)",
  border: "1px solid var(--em-border)",
  borderRadius: 8,
  padding: "9px 16px",
  cursor: "pointer",
  fontSize: 13,
  fontWeight: 600,
};

export {
  Field,
  input,
  overlay,
  modal,
  modalHead,
  iconBtn,
  primaryBtn,
  ghostBtn,
};
